import { LOCALES } from "@divespots/contracts";
import type { Locale } from "@divespots/contracts";
import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/Button.tsx";
import { SegmentedControl } from "../components/SegmentedControl.tsx";
import { missingLocales } from "./forms.ts";
import type { FieldErrors, LocaleTexts } from "./forms.ts";

/** A labelled form control with an optional hint and an inline error (announced via aria-describedby). */
export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  /** Receives the ids to wire onto the control. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  htmlFor?: string;
}) {
  const uid = useId();
  const id = htmlFor ?? `${uid}-c`;
  const hintId = hint ? `${uid}-h` : undefined;
  const errId = error ? `${uid}-e` : undefined;
  return (
    <div className="adm-field">
      <label htmlFor={id} className="ds-text-subheadline adm-field__label">
        {label}
      </label>
      {children({
        id,
        describedBy: [hintId, errId].filter(Boolean).join(" ") || undefined,
        invalid: Boolean(error),
      })}
      {hint ? (
        <p id={hintId} className="ds-text-footnote text-label-secondary m-0">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errId} className="adm-error ds-text-footnote m-0" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function Section({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="adm-section">
      <div className="adm-section__head">
        <h2 id={id} className="ds-text-title-3 m-0">
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** One locale selector for a group of translatable fields; marks locales that miss a translation. */
export function LocaleTabs({
  value,
  onChange,
  fields,
  label,
}: {
  value: Locale;
  onChange: (l: Locale) => void;
  fields: readonly LocaleTexts[];
  label: string;
}) {
  const { t } = useTranslation();
  const missing = new Set(missingLocales(fields));
  return (
    <SegmentedControl
      aria-label={label}
      value={value}
      onChange={onChange}
      options={LOCALES.map((l) => ({
        value: l,
        label: l.toUpperCase(),
        ...(missing.has(l) ? { hint: t("admin.i18n.missing") } : {}),
      }))}
    />
  );
}

/** A single text (input or textarea) editing one locale of a `LocaleTexts` value. */
export function LocaleField({
  label,
  value,
  locale,
  onChange,
  multiline,
  error,
  required,
}: {
  label: string;
  value: LocaleTexts;
  locale: Locale;
  onChange: (next: LocaleTexts) => void;
  multiline?: boolean;
  error?: string | undefined;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const missing = locale !== "de" && value.de.trim() !== "" && value[locale].trim() === "";
  return (
    <Field
      label={`${label} (${locale.toUpperCase()})${required ? " *" : ""}`}
      error={error}
      hint={missing ? t("admin.i18n.missingHint", { lang: t(`language.${locale}`) }) : undefined}
    >
      {({ id, describedBy, invalid }) =>
        multiline ? (
          <textarea
            id={id}
            lang={locale}
            className="adm-input adm-textarea"
            rows={4}
            maxLength={5000}
            value={value[locale]}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
          />
        ) : (
          <input
            id={id}
            lang={locale}
            className="adm-input"
            value={value[locale]}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
          />
        )
      }
    </Field>
  );
}

/** Looks up an error for a dotted path, falling back to a parent key. */
export const errorAt = (errors: FieldErrors, ...keys: string[]) =>
  keys.map((k) => errors[k]).find(Boolean);

/**
 * Modal confirmation sheet (never `window.confirm`): focus moves to the cancel button, Escape and
 * the backdrop cancel, the destructive action is explicit.
 */
export function ConfirmSheet({
  title,
  body,
  confirmLabel,
  cancelLabel,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  busy?: boolean;
  error?: string | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div className="adm-backdrop" onClick={onCancel}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="adm-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="adm-sheet__scroll">
          <h2 id={titleId} className="ds-text-title-3 m-0">
            {title}
          </h2>
          <p id={bodyId} className="ds-text-body m-0">
            {body}
          </p>
          {error ? (
            <p className="adm-error ds-text-footnote m-0" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className="adm-sheet__actions">
          <button
            ref={cancelRef}
            type="button"
            className="ds-btn ds-btn--tinted"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel ?? t("admin.common.cancel")}
          </button>
          <Button
            variant="destructive"
            className="adm-destructive"
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
